import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../../../utils/cmsAxios";
import SortableTable from "../CommonComponents/SortableTable";
import PeriodPicker, { toKstIsoRange } from "../CommonComponents/PeriodPicker";
import { FiPlus } from "react-icons/fi";
import { Calendar } from "lucide-react";



const CmsNotice = ( {type = "admin"} ) => {
    const navigate = useNavigate();
    const location = useLocation();

    // 쿼리/STATE/로컬스토리지 순으로 우선순위
    const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const qsType = qs.get("type");                  // ex) ?type=admin
    const stateType = location.state?.type;         // Link에서 넘긴 state
    const storedType = useMemo(() => localStorage.getItem("cms.type"), []);

    // 1) state.type === 'admin' 우선, 2) ?type=admin 쿼리 허용
    const isAdmin = useMemo(() => {
        return (qsType ?? stateType ?? storedType ?? type) === "admin";
    }, [qsType,  stateType, storedType, type]);

      // STATE로 들어온 경우, URL에 ?type=admin을 꽂아서 새로고침에도 유지
    useEffect(() => {
        if (stateType === "admin" && qsType !== "admin") {
        const p = new URLSearchParams(location.search);
        p.set("type", "admin");
        navigate({ pathname: location.pathname, search: p.toString() }, { replace: true });
        }
    }, [stateType, qsType, navigate, location.pathname, location.search]);

    // 현재 모드 저장(선택 사항: 다음 방문 시 기본값으로 활용)
    useEffect(() => {
        if (isAdmin) localStorage.setItem("cms.type", "admin");
        else localStorage.removeItem("cms.type");
    }, [isAdmin]);

    // ✅ 검색/필터 상태 추가
    const [field, setField] = useState("all");      // all | title | content | writer
    const [keyword, setKeyword] = useState("");
    const [post, setPost] = useState("all");
    const [noticeType, setNoticeType] = useState("all");
    const [showJoinPicker, setShowJoinPicker] = useState(false);
    const [joinMode, setJoinMode] = useState("single"); // 'single' | 'range'
    const [joinSingle, setJoinSingle] = useState(null); // Date | null
    const [joinRange, setJoinRange] = useState({ from: null, to: null }); // {from,to}

    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [selectedIds, setSelectedIds] = useState(new Set());

    const [applied, setApplied] = useState({
        field: "all",
        keyword: "",
        post: "all",
        noticeType: "all",
        joinStart: "",
        joinEnd: "",
    });
    
    const getCurrentJoinParams = useCallback(() => {
        if (joinMode === "single" && joinSingle) {
            return toKstIsoRange(joinSingle, joinSingle);
        }
        if (joinMode === "range" && joinRange.from) {
            return toKstIsoRange(joinRange.from, joinRange.to || joinRange.from);
        }
        return {};
    }, [joinMode, joinSingle, joinRange]);

    // ✅ fetch 함수 파라미터/쿼리스트링 지원으로 교체
    const fetchNotices = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            // const qs = new URLSearchParams(params).toString();
            // const { data } = await axios.get(`${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/get/notice${qs ? `?${qs}` : ""}`);
            const { data } = await axios.get(`${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/get/notice`,
                {params: {include_hidden: isAdmin, ...params,},}
            );
            console.log("Fetched notices:", data);
            setNotices(Array.isArray(data) ? data : []);
            setSelectedIds(new Set());
        } catch (e) {
            setErr("목록을 불러오지 못했습니다.");
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [isAdmin]);

    const filteredNotices = useMemo(() => {
        const { field: f, keyword: kw, post: p, noticeType: t, joinStart: js, joinEnd: je } = applied;
        const q = (kw ?? "").trim().toLowerCase();

        const hasRange = Boolean(js && je);
        const startMs = hasRange ? new Date(js).getTime() : null;
        const endMs   = hasRange ? new Date(je).getTime() : null;

        return (notices || []).filter((n) => {
            // 노출여부
            if (p !== "all") {
            const status = n.notice_post === "Y" ? "Y" : "N";
            if (status !== p) return false;
            }

            if (t !== "all") {
            const category = n.notice_type === "이벤트" ? "이벤트" : "일반";
            if (category !== t) return false;
            }

            // 기간(등록/수정일) 필터
            if (hasRange) {
            const t = new Date(n.updated_at ?? n.created_at ?? 0).getTime();
            if (!Number.isFinite(t) || t < startMs || t > endMs) return false;
            }

            // 키워드
            if (!q) return true;

            const title = String(n.notice_title ?? "").toLowerCase();
            const writer = String(n.create_user ?? n.writer ?? "admin").toLowerCase();
            const content = String(n.notice_content ?? "").toLowerCase(); // 백엔드 필드명에 맞춰 필요시 수정

            if (f === "title")  return title.includes(q);
            if (f === "writer") return writer.includes(q);
            if (f === "content") return content.includes(q);

            // all
            return title.includes(q) || writer.includes(q) || content.includes(q);
        });
    }, [notices, applied]);


    const tableData = useMemo(() => {
        return (filteredNotices || []).map((n) => ({
            id: n.notice_no,
            title: n.notice_title ?? "",
            // writer: n.create_user ?? n.writer ?? "admin",
            status: n.notice_post === "Y" ? "Y" : "N",
            category: n.notice_type === "이벤트" ? "이벤트" : "일반",
            created_at: n.updated_at ?? n.created_at,
            views: n.views ?? 0,
            selectable: true,
            _raw: n, // 원본 보존(행 클릭 시 사용)
        }));
    }, [filteredNotices]);

    useEffect(() => {
        fetchNotices();
    }, [fetchNotices]);

    const handleNoticeClick = (notice) => {
        navigate(`/cms/notice/edit/${notice.notice_no}`, {
            state: { notice, type: "admin" }
        });
    };

    // const handleEditClick = (notice) => {
    //     navigate(`/cms/notice/edit/${notice.notice_no}`, {
    //         state: { notice, type: "admin" }
    //     });
    // };

    // const handleDeleteClick = async (notice_no) => {
    //     if (!window.confirm("정말 이 공지사항을 삭제하시겠습니까?")) return;
    //     try {
    //         const res = await axios.post(`${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/delete/notice/${notice_no}`);
    //         if (res.data?.success) {
    //             alert("삭제되었습니다.");
    //             fetchNotices();
    //         } else {
    //             alert("삭제에 실패했습니다.");
    //         }
    //     } catch (e) {
    //         console.error(e);
    //         alert("서버 오류로 삭제에 실패했습니다.");
    //     }
    // };

    
    // ✅ 검색/리셋 핸들러 추가
    const handleSearch = () => {
        const cur = getCurrentJoinParams();
        setApplied({
            field,
            keyword,
            post,
            noticeType,
            joinStart: cur.join_start || "",
            joinEnd: cur.join_end || "",
        });

        // const js = cur.join_start || "";
        // const je = cur.join_end || "";
        // fetchNotices({
        //     field,
        //     keyword,
        //     ...(post !== "all" && { notice_post: post }),
        //     ...(js && je ? {
        //         join_start: js,
        //         join_end: je,
        //         joinStart: js,
        //         joinEnd: je,
        //     } : {}),
        // });
    };

    const handleReset = () => {
        setField("all");
        setKeyword("");
        setPost("all");
        setNoticeType("all");
        setShowJoinPicker(false);
        setJoinMode("single");
        setJoinSingle(null);
        setJoinRange({ from: null, to: null });   
        setApplied({ field: "all", keyword: "", post: "all", noticeType:"all", joinStart: "", joinEnd: "" });
        fetchNotices();
        setSelectedIds(new Set());
    };
    
    // ✅ 일괄 삭제 핸들러 추가
    const handleBulkDelete = async () => {
        if (!selectedIds.size) {
            alert("삭제할 항목을 선택하세요.");
            return;
        }
        if (!window.confirm(`선택한 ${selectedIds.size}건을 삭제하시겠습니까?`)) return;

        for (const id of selectedIds) {
            try {
                await axios.post(`${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/delete/notice/${id}`);
            } catch (e) {
                console.error("삭제 실패:", id, e);
            }
        }
        fetchNotices();
    };

    const selectableTotal = useMemo(
        () => tableData.filter(r => r.selectable).length,
        [tableData]
    );

    // ✅ 전체/개별 체크 토글 추가
    const allChecked = useMemo(() => (
        selectableTotal > 0 && selectedIds.size === selectableTotal
    ), [selectableTotal, selectedIds]);

    const toggleAll = useCallback(() => {
        setSelectedIds((prev) => {
            const shouldUncheck = prev.size === selectableTotal;
            if (shouldUncheck) return new Set();
            return new Set(tableData.filter(r => r.selectable).map(r => r.id));
        });
    }, [tableData, selectableTotal]);

    const toggleOne = useCallback((id) => {
        const row = tableData.find(r => r.id === id);
        if (!row || !row.selectable) return;   // 선택 불가면 무시
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, [tableData]);
    // const allChecked = notices.length > 0 && selectedIds.size === notices.length;

    // const toggleAll = () => {
    //     if (allChecked) setSelectedIds(new Set());
    //     else setSelectedIds(new Set(notices.map((n) => n.notice_no)));
    // };

    // const toggleOne = (id) => {
    //     setSelectedIds((prev) => {
    //         const next = new Set(prev);
    //         if (next.has(id)) next.delete(id);
    //         else next.add(id);
    //         return next;
    //     });
    // };

    // const formatDate = useCallback((dateString) => {
    //     const d = new Date(dateString);
    //     if (Number.isNaN(d.getTime())) return "";
    //     const y = d.getFullYear();
    //     const m = String(d.getMonth() + 1).padStart(2, "0");
    //     const dd = String(d.getDate()).padStart(2, "0");
    //     return `${y}-${m}-${dd}`;
    // }, []);    
    
    const withDateTooltip = useCallback((v) => {
        if (!v) return "-";
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return "-";
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const mm = String(d.getMinutes()).padStart(2, "0");
        const ss = String(d.getSeconds()).padStart(2, "0");
        const tip = `${y}.${m}.${dd} ${hh}:${mm}:${ss}`;
        return <time dateTime={d.toISOString()} title={tip}>{`${y}.${m}.${dd}`}</time>;
    }, []);

    const columns = useMemo(() => ([
        {
        key: "_select",
        header: "",                 // 헤더 텍스트 없이
        sortable: false,            // 정렬 비활성
        headerRender: () => (       // 전체 선택 체크박스
            <input
                type="checkbox"
                checked={allChecked}
                onChange={toggleAll}
                disabled={selectableTotal === 0}
                className="w-4 h-4"
                aria-label="전체 선택"
            />
        ),
        render: (_v, row) => (
            <input
                type="checkbox"
                checked={selectedIds.has(row.id)}
                disabled={!row.selectable}               // ✅ Y면 비활성
                title={!row.selectable ? "노출 중(Y) 항목은 삭제할 수 없습니다." : ""}
                onChange={(e) => { e.stopPropagation(); toggleOne(row.id); }}
                onClick={(e) => e.stopPropagation()}
                className="w-4 h-4"
                aria-label="선택"
            />
        ),
    },
        // { key: "_rownum", header: "번호", type: "number", sortable: true, render: (_v, _r, index) => index + 1, },
        { key: "status", header: "노출여부", type: "string", align: "center", render: (v) => (v) },
        { key: "category", header: "카테고리", type: "string", align: "center", render: (v) => (v) },
        { key: "title", header: "제목", type: "string", render: (v) => <span className="line-clamp-1">{v}</span> },
        { key: "views", header: "조회수", type: "number", align: "center"},
        { key: "writer", header: "등록(수정)자", type: "string", align: "center", render: () => "admin" },
        { key: "created_at", header: "등록(수정)일", type: "date", align: "center", render: withDateTooltip }
    ]), [allChecked, selectableTotal, selectedIds, toggleAll, toggleOne, withDateTooltip]);


    const handleCreateClick = () => {
        navigate("/cms/notice/create", { state: { type: "admin" } });
    };

    return (
        <div className="min-h-screen bg-neutral-50">
            <div className="mx-auto max-w-[1280px] bg-white rounded-2xl shadow overflow-hidden">
                <div className="flex">
                    
                    <div className="flex-1 min-w-0 bg-neutral-50/70">
                        <main className="pt-12 flex flex-col p-4 overflow-x-hidden w-full">
                            <section className="p-4 w-full overflow-x-hidden">
                                <div className="mb-3 flex items-center justify-between">
                                    <h2 className="text-xl font-semibold mb-4">공지사항</h2>
                                </div>

                                {err && (
                                    <div className="mb-3 rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">
                                        {err}
                                    </div>
                                )}

                                {/* 검색/필터 바 */}
                                <div className="rounded-lg bg-white p-3">
                                    <div className="flex flex-wrap items-end gap-6">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-sm text-neutral-500">검색</span>
                                            <div className="flex items-center gap-2">
                                                <select
                                                    value={field}
                                                    onChange={(e) => setField(e.target.value)}
                                                    className="h-9 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                                                >
                                                    <option value="all">전체</option>
                                                    <option value="title">제목</option>
                                                    {/* <option value="content">내용</option> */}
                                                    <option value="writer">등록(수정)자</option>
                                                </select>
                                                <input
                                                    value={keyword}
                                                    onChange={(e) => setKeyword(e.target.value)}
                                                    type="text"
                                                    placeholder="검색어를 입력하세요"
                                                    className="h-9 w-52 rounded-md border border-neutral-300 bg-white px-3 text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-sm text-neutral-500">노출 여부</span>
                                            <select
                                                value={post}
                                                onChange={(e) => setPost(e.target.value)}
                                                className="h-9 w-24 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                                            >
                                                <option value="all">전체</option>
                                                <option value="Y">Yes</option>
                                                <option value="N">No</option>
                                            </select>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-sm text-neutral-500">카테고리</span>
                                            <select
                                                value={noticeType}
                                                onChange={(e) => setNoticeType(e.target.value)}
                                                className="h-9 w-24 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                                            >
                                                <option value="all">전체</option>
                                                <option value="일반">일반</option>
                                                <option value="이벤트">이벤트</option>
                                            </select>
                                        </div>
                                        {/* <div className="hidden h-6 w-px bg-neutral-200 md:block" /> */}
                                        <div className="flex flex-col gap-1">
                                            <span className="text-sm text-neutral-500">등록(수정)일</span>
                                            <button
                                                type="button"
                                                onClick={() => setShowJoinPicker(true)}
                                                className="h-9 w-[230px] inline-flex items-center justify-between gap-2
                                                        rounded-md border border-neutral-300 bg-white px-2 text-sm whitespace-nowrap"
                                            >
                                                <span className="truncate">
                                                    {(() => {
                                                        const cur = getCurrentJoinParams();
                                                        if (cur.join_start && cur.join_end) {
                                                            const s = new Date(cur.join_start);
                                                            const e = new Date(cur.join_end);
                                                            const fmt = (d) =>
                                                            `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                                                            return `${fmt(s)} ~ ${fmt(e)}`;
                                                        }
                                                        return "-";
                                                    })()}
                                                </span>
                                                <Calendar />
                                            </button>

                                            {showJoinPicker && (
                                                <>
                                                    <div
                                                        className="fixed inset-0 bg-black/30 z-[40]"
                                                        onClick={() => setShowJoinPicker(false)}
                                                    />
                                                    <div className="absolute z-[50] mt-2 rounded-lg border bg-white shadow-lg p-3">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <select
                                                                value={joinMode}
                                                                onChange={(e) => setJoinMode(e.target.value)}
                                                                className="h-8 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                                                            >
                                                            <option value="single">하루</option>
                                                            <option value="range">기간</option>
                                                            </select>
                                                        </div>
                                                    
                                                        <PeriodPicker
                                                            key={`pp-${joinMode}-${joinSingle ? +joinSingle : 0}-${joinRange.from ? +joinRange.from : 0}-${joinRange.to ? +joinRange.to : 0}`}
                                                            mode={joinMode}
                                                            value={joinMode === "single" ? joinSingle : joinRange}
                                                            onChange={(val) => {
                                                                if (joinMode === "single") {setJoinSingle(val || null);}
                                                                else {setJoinRange(val ?? { from: null, to: null });}
                                                                }}
                                                            onConfirm={() => setShowJoinPicker(false)}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-3 flex items-center justify-center gap-3">
                                        <button
                                            type="button"
                                            onClick={handleReset}
                                            className="h-9 px-3 rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                                        >
                                            초기화
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleSearch()}
                                            className="h-9 px-3 rounded-md bg-[#7864F9] text-white hover:bg-indigo-700 shadow-sm"
                                        >
                                            검색
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 py-3">
                                    <div className="text-sm text-neutral-600">
                                        총 <span className="text-violet-500">{filteredNotices.length} 건</span>
                                    </div>
                                    <div className="ml-auto flex gap-2">
                                        <button
                                            type="button"
                                            onClick={handleCreateClick}
                                            className="rounded-lg text-sm px-3 py-1.5 bg-[#7864F9]/10 text-[#7864F9] inline-flex items-center"
                                        >
                                            <FiPlus className="mr-1"/>등록
                                        </button>
                                        <span aria-hidden="true" className="mx-2 h-7 w-[2px] mt-[5px] bg-[#D9D9D9]"/>
                                        <button
                                            type="button"
                                            onClick={handleBulkDelete}
                                            className="rounded-lg border px-3 py-1.5 text-sm bg-black/10 text-black/50"
                                        >
                                            삭제
                                        </button>
                                    </div>
                                </div>

                                <div className="border rounded-lg bg-white overflow-hidden p-4 text-sm">
                                    {loading ? (
                                        <div className="p-10 text-center text-gray-500">불러오는 중…</div>
                                    ) : notices.length === 0 ? (
                                        <div className="p-10 text-center text-gray-500">공지사항이 없습니다.</div>
                                    ) : (
                                        <SortableTable
                                            data={tableData}
                                            columns={columns}
                                            defaultSort={{ key: "created_at", dir: "desc" }}
                                            rowKey="id"
                                            onRowClick={(row) => handleNoticeClick(row._raw)}
                                            tableClassName="min-w-full border-separate border-spacing-0"
                                            headerCellClassName="bg-neutral-50 text-black/50 first:rounded-tl-xl last:rounded-tr-xl text-sm"
                                            bodyClassName="text-sm text-black/50"
                                            columnWidths={["4ch","12ch","auto","10ch","16ch","16ch"]}
                                        />
                                    )}
                                </div>
                            </section>
                        </main>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CmsNotice;
